import React from 'react';

import type { Role } from '~/@types/redux';
import { useUser } from '~/common/hooks/useAuthentication';

type RoleProtectedProps = React.PropsWithChildren<{
  roles?: Role[];
  notInRoles?: Role[];
}>;

/**
 * Renders its children only if the user is in one of the specified roles
 * and not in any of the excluded roles.
 *
 * @example
 * // Basic role check
 * <RoleProtected roles={[USER_ROLE.SR_UNDERWRITER]}>
 *   <div>This is a protected feature!</div>
 * </RoleProtected>
 *
 * @example
 * // Exclude specific roles
 * <RoleProtected notInRoles={[USER_ROLE.PROCESSOR]}>
 *   <div>Everyone except processors can see this</div>
 * </RoleProtected>
 *
 * @example
 * // Both roles and notInRoles
 * <RoleProtected roles={[USER_ROLE.UNDERWRITER]} notInRoles={[USER_ROLE.SR_UNDERWRITER]}>
 *   <div>Non senior underwriters, only</div>
 * </RoleProtected>
 *
 * @param props
 * @param props.roles The roles that are allowed to see the children. If empty or undefined, all roles pass this check.
 * @param props.notInRoles The roles that are explicitly excluded from seeing the children.
 */
export function RoleProtected({
  roles = [],
  notInRoles = [],
  children,
}: Readonly<RoleProtectedProps>) {
  const { isUnderwriter, role: userRole } = useUser();

  const userRoles = [...userRole];
  if (isUnderwriter) {
    userRoles.push('Underwriter');
  }

  const hasRoleAccess =
    roles.length === 0 || userRoles.some((role) => roles.includes(role));

  const isExcluded =
    notInRoles.length > 0 &&
    userRoles.some((role) => notInRoles.includes(role));

  const shouldRender = hasRoleAccess && !isExcluded;

  if (!shouldRender) {
    return null;
  }

  return <>{children}</>;
}
